import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { ConnectorInfo, TriggerTypeInfo } from '../model/recipe-catalog.model';
import {
    compareConnectorsForDisplay,
    connectorIcon,
    SURFACED_SYSTEM_CONNECTORS,
    triggerIcon,
} from './recipe-catalog.component';

/**
 * The recipe icon strip as the catalog card builds it (see `RecipeCatalogComponent.cardIcons`),
 * for the workflow-recipe recommendation dialog. Reuses the catalog's icon helpers as they are,
 * so the catalog itself is left untouched.
 */

/** What the strip needs off a manifest or a manifest detail — both satisfy it. */
export type RecipeIconSource = {
    connectors?: string[];
    connector_details?: ConnectorInfo[];
    trigger_type_details?: TriggerTypeInfo[];
};

/**
 * The connectors a recipe is built against, as they should be presented: backend-provided
 * `connector_details` merged with the allowlisted "system" connectors it deliberately
 * hides (email, etc.), then ordered.
 *
 * Anything naming a recipe's connectors goes through here rather than reading
 * `connector_details` on its own — the icon strip and the "Required Connections" line otherwise
 * disagree about Email, showing the icon while omitting the name.
 */
export function buildRecipeConnectors(source: RecipeIconSource): ConnectorInfo[] {
    const seen = new Set<string>();
    const connectors: ConnectorInfo[] = [];
    for (const c of source.connector_details ?? []) {
        connectors.push(c);
        seen.add(c.id);
    }
    for (const rawId of source.connectors ?? []) {
        const surfaced = SURFACED_SYSTEM_CONNECTORS[rawId];
        if (surfaced && !seen.has(rawId)) {
            connectors.push({ id: rawId, display_name: surfaced.display_name });
            seen.add(rawId);
        }
    }

    // Fixed order across recipes: Pure1 always first, everything else
    // alphabetical by display_name. The backend does not guarantee a stable
    // order for connector_details, so identical recipes rendered side by
    // side otherwise show the same icons in different positions.
    connectors.sort(compareConnectorsForDisplay);
    return connectors;
}

/**
 * Builds the icon strip: the trigger type first, then the connectors the recipe is
 * built against, in a fixed order.
 */
export function buildRecipeIcons(source: RecipeIconSource): { icon: IconDefinition; label: string }[] {
    const icons: { icon: IconDefinition; label: string }[] = [];
    const trigger = source.trigger_type_details?.[0];
    if (trigger) {
        icons.push({ icon: triggerIcon(trigger.display_name), label: trigger.display_name });
    }

    for (const c of buildRecipeConnectors(source)) {
        icons.push({ icon: connectorIcon(c.display_name), label: c.display_name });
    }
    return icons;
}
